import { ChangeEvent } from "react";
import { extractApiErrorMessage } from "../../../../utils/util";
import DSAlert from "../../../system-design/DSAlert";
import DSBox from "../../../system-design/DSBox";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSHint from "../../../system-design/DSHint";
import DSTypography from "../../../system-design/DSTypography";
import DSUploadButton from "../../../system-design/DSUploadButton";
import {
  useClearMomentCoverMutation,
  useMomentSettingsQuery,
  useUploadMomentCoverMutation,
} from "../hooks/useMomentSettings";

const previewStyle = {
  width: 200,
  aspectRatio: "4 / 3",
  objectFit: "cover" as const,
  borderRadius: 8,
};

// The hero of the app's Moments tab: a photo, or a looping video. What is
// uploaded decides it (mp4 = video, anything else = photo). The app draws the
// title and tagline on top of it.
const MomentCoverPhoto = () => {
  const { data: settings, error: loadError } = useMomentSettingsQuery();
  const {
    mutate: upload,
    isPending: isUploading,
    error: uploadError,
  } = useUploadMomentCoverMutation();
  const {
    mutate: clear,
    isPending: isClearing,
    error: clearError,
  } = useClearMomentCoverMutation();
  const error = loadError ?? uploadError ?? clearError;

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => upload(reader.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <DSCard sx={{ mb: 3 }}>
      <DSCardContent>
        <DSHint label="Moments tab cover">
          The picture or video behind the title at the top of the Moments tab in
          the app. Upload a <b>photo</b> to show a still image, or an <b>mp4</b>{" "}
          to show a looping video.
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            <li>
              Recommended size: <b>1200 × 900 px</b> (4:3, landscape). Other
              ratios are cropped to 4:3 from the centre.
            </li>
            <li>Photo: JPG or PNG, under 1 MB so the tab opens fast.</li>
            <li>
              Video: <b>mp4 (H.264)</b>, 4:3, a seamless loop of 3 to 6 seconds,{" "}
              <b>no sound</b>, under <b>5 MB</b> (aim for 3 MB). The first frame
              is used as the still shown while it loads.
            </li>
            <li>
              The title and tagline are drawn by the app over the top of the
              picture: keep the top quarter calm, the subject in the middle, and
              leave out text.
            </li>
            <li>
              Dark or warm, evening-like photos match the app best. Without a
              cover the app shows a plain gradient.
            </li>
          </ul>
        </DSHint>
        {error && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            {extractApiErrorMessage(error, "Could not update the cover")}
          </DSAlert>
        )}
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
          {settings?.coverUrl ? (
            settings.coverType === "video" ? (
              <video
                src={settings.coverUrl}
                poster={settings.coverPosterUrl}
                style={previewStyle}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img
                src={settings.coverUrl}
                alt="Moments cover"
                style={previewStyle}
              />
            )
          ) : (
            <DSTypography variant="body2">No cover yet.</DSTypography>
          )}
          <label>
            <input
              type="file"
              accept="image/png,image/jpeg,video/mp4"
              hidden
              onChange={onPick}
            />
            <DSUploadButton
              text={settings?.coverUrl ? "Replace cover" : "Upload cover"}
            />
          </label>
          {settings?.coverUrl && (
            <DSErrorButton disabled={isClearing} onClick={() => clear()}>
              Remove
            </DSErrorButton>
          )}
          {(isUploading || isClearing) && <DSCircularProgress size={20} />}
        </DSBox>
      </DSCardContent>
    </DSCard>
  );
};

export default MomentCoverPhoto;
